import { useState } from 'react';
import { useConnectionsStore } from '../../../store/connectionsStore';
import SecureInput from '../../../ui/SecureInput/SecureInput';
import ConnectorSelect from '../../../ui/ConnectorSelect/ConnectorSelect';
import MultiSelect from '../../../ui/MultiSelect/MultiSelect';
import TagEditor from '../../../components/TagEditor/TagEditor';
import AiGenerator from '../../../components/aiGenerator/AiGenerator';
import { AiGenerationTypes } from '../../../enums/aiGenerationTypes';
import InfoLabel from '../../../ui/InfoLabel/InfoLabel';
import { AlertTriangle } from 'lucide-react';
import { useAuthStore } from '../../../store/authStore';
import { hasMinRole } from '../../../lib/permissions';
import { UserRole } from '../../../enums/userRole';
import InfoCard from '../../../ui/InfoCard/InfoCard';
import styles from './ToolInfoStep.module.css';

interface ToolInfoStepProps {
  watch: any;
  setValue: any;
  register: any;
  errors: any;
  canEdit: boolean;
  setError: any;
  clearErrors: any;
  trigger: any;
  checkDuplicateName: (name: string) => boolean;
}

export default function ToolInfoStep({
  watch,
  setValue,
  register,
  errors,
  canEdit,
  setError,
  clearErrors,
  trigger,
  checkDuplicateName,
}: ToolInfoStepProps) {
  const [aiLoading, setAiLoading] = useState(false);
  const connectors = useConnectionsStore(s => s.connectors);
  const currentUser = useAuthStore(s => s.user);
  const isAdminOrAbove = !!(currentUser && hasMinRole(currentUser.role?.id, UserRole.Admin));
  const selectedConnector = connectors.find(c => c.id === watch('mainConnectionId'));

  const mapIdsToObjects = (ids: string[]) => {
    return ids
      .map(id => {
        const connection = connectors.find(con => con.id === id);
        return connection
          ? { id: String(connection.id), name: String(connection.connectionName), environment: connection.environment as string | undefined }
          : undefined;
      })
      .filter(Boolean) as { id: string; name: string; environment?: string }[];
  };

  return (
    <div className={styles.step}>
      <div className={styles.field}>
        <SecureInput
          fieldType="name"
          label="Display name"
          description="Use a clear business-facing title so users understand what this tool does."
          value={watch('title') || ''}
          onChange={(value) => {
            clearErrors('title');
            const prevTitle = watch('title') || '';
            setValue('title', value, { shouldValidate: true, shouldDirty: true });
            // Auto-generate tool name from title only if name is empty or was auto-derived from the previous title
            const currentName = (watch('name') || '') as string;
            const derivedFromPrev = prevTitle
              .trim()
              .toLowerCase()
              .replace(/[^a-z0-9\s_-]/g, '')
              .replace(/[\s-]+/g, '_');
            if (!currentName || currentName === derivedFromPrev) {
              const toolName = value
                .trim()
                .toLowerCase()
                .replace(/[^a-z0-9\s_-]/g, '')
                .replace(/[\s-]+/g, '_');
              setValue('name', toolName, { shouldValidate: true, shouldDirty: true });
              if (checkDuplicateName(toolName)) {
                setError('name', { type: 'manual', message: 'A tool with this name already exists.' });
              } else {
                clearErrors('name');
                void trigger('name');
              }
            }
          }}
          placeholder="e.g. Get Data"
          disabled={!canEdit}
          required
          className={styles.input}
          externalError={errors.title?.message}
        />
      </div>
      <div className={styles.field}>
        <SecureInput
          fieldType="name"
          label="Technical Name"
          description="Use lowercase letters, numbers, and underscores. This is how the AI will reference your tool."
          value={watch('name') || ''}
          onChange={(value) => {
            setValue('name', value, { shouldValidate: true, shouldDirty: true });
            if (checkDuplicateName(value)) {
              setError('name', { type: 'manual', message: 'A tool with this name already exists.' });
            } else {
              clearErrors('name');
              void trigger('name');
            }
          }}
          placeholder="e.g. get_inventory_data"
          disabled={!canEdit}
          required
          className={styles.input}
          externalError={errors.name?.message}
        />
      </div>

      <div className={`${styles.field} ${styles.compactField}`}>
        <div className={styles.labelRow}>
          <label className={styles.label}>Description</label>
          <InfoLabel content="Write a clear description so the AI knows when and how to use this tool." />
        </div>
        <textarea
          {...register('description', {
            onChange: () => clearErrors('description'),
          })}
          className={`${styles.textarea} ${errors.description ? styles.textareaError : ''}`}
          rows={3}
          placeholder="Describe what this tool does, e.g. 'Retrieves inventory risk data for a given supplier and plant'"
          disabled={!canEdit}
          aria-invalid={!!errors.description}
        />
        {errors.description && (
          <span className={styles.error}>{errors.description.message}</span>
        )}
        {canEdit && (
          <AiGenerator
            prompt={watch('description')}
            loading={aiLoading}
            setContent={(value) => {
              clearErrors('description');
              setValue('description', value, { shouldValidate: true, shouldDirty: true });
            }}
            setAiLoading={setAiLoading}
            generateBody={{
              type: AiGenerationTypes.description,
              description: watch('description'),
              context: { tool_name: watch('name') },
            }}
          />
        )}
      </div>

      <div className={`${styles.field} ${styles.compactField}`}>
        <div className={styles.labelRow}>
          <label className={styles.label}>Tags</label>
          <InfoLabel content="Organize your tool with tags for easy filtering." />
        </div>
        <TagEditor
          tags={watch('tags') || []}
          canEdit={canEdit}
          setValue={(field: string, value: unknown) => setValue(field, value, { shouldDirty: true })}
        />
      </div>

      <div className={`${styles.divider} ${styles.fullWidth}`} />

      <div className={`${styles.field} ${styles.compactField}`}>
        <div className={styles.labelRow}>
          <label className={styles.label}>System Connection</label>
          <InfoLabel content="Choose which backend system this tool connects to." />
        </div>
        <InfoCard
          title="Connector Info"
          rows={isAdminOrAbove && selectedConnector ? [
            { label: 'Name', value: selectedConnector.connectionName },
            { label: 'Path', value: selectedConnector.config?.baseUrl ?? '-' },
            { label: 'Env', value: selectedConnector.environment ?? '-' },
            { label: 'Auth Type', value: selectedConnector.authType },
            { label: 'Active', value: selectedConnector.isActive ? 'Yes' : 'No' },
          ] : []}
        >
          <ConnectorSelect
            options={connectors.filter(con => con.isActive).map(con => ({
              id: con.id,
              name: con.connectionName,
              environment: con.environment,
            }))}
            value={watch('mainConnectionId') || ''}
            onChange={(id) => {
              clearErrors('mainConnectionId');
              const currentConnector = connectors.find(c => c.id === watch('mainConnectionId'));
              const newConnector = connectors.find(c => c.id === id);
              const wasMssql = currentConnector?.connectionTypeId === 7;
              const willBeMssql = newConnector?.connectionTypeId === 7;
              setValue('mainConnectionId', id, { shouldValidate: true, shouldDirty: true });
              // Auto-set httpMethod based on connector type
              if (willBeMssql) {
                setValue('httpMethod', 'SELECT', { shouldDirty: true });
              } else if (watch('httpMethod') === 'SELECT') {
                setValue('httpMethod', 'GET', { shouldDirty: true });
              }
              // Clear response mapping when switching TO MSSQL so the stepper updates immediately
              if (!wasMssql && willBeMssql) {
                setValue('responseTemplate', '', { shouldDirty: true });
                setValue('sampleResponse', '', { shouldDirty: true });
              }
            }}
            disabled={!canEdit}
            hasError={!!errors.mainConnectionId?.message}
          />
        </InfoCard>
        {errors.mainConnectionId?.message && (
          <span className={styles.error}>{errors.mainConnectionId.message}</span>
        )}
        {(() => {
          const selectedId = watch('mainConnectionId');
          if (!selectedId) return null;
          const selected = connectors.find(c => c.id === selectedId);
          if (selected && !selected.isActive) {
            return (
              <div className={styles.connectionWarning}>
                <AlertTriangle size={16} />
                <span>This connector is inactive. The tool will be disabled until the connector is activated.</span>
              </div>
            );
          }
          return null;
        })()}
      </div>

      <div className={`${styles.field} ${styles.compactField}`}>
        <div className={styles.labelRow}>
          <label className={styles.label}>Additional Connections</label>
          <InfoLabel content="Optionally connect to additional backend systems." />
        </div>
        <MultiSelect
          options={connectors
            .filter(con => !(watch('mainConnectionId') || []).includes(con.id))
            .map(con => ({
              id: con.id,
              name: con.connectionName,
              environment: con.environment,
            }))}
          selected={mapIdsToObjects(watch('subConnectionsIds') || [])}
          onChange={(selected: any) => {
            setValue(
              'subConnectionsIds',
              selected.map((s: any) => s.id), 
              { shouldDirty: true }
            );
          }}
          placeholder="Select additional connections"
          disabled={!canEdit}
        />
      </div>

      <div className={`${styles.divider} ${styles.fullWidth}`} />

      <div className={styles.field}>
        <SecureInput
          fieldType="text"
          label="Version"
          description="Track changes to the tool configuration over time."
          value={watch('version') || ''}
          onChange={(value) => {
            clearErrors('version');
            setValue('version', value, { shouldValidate: true, shouldDirty: true });
          }}
          placeholder="1.0.0"
          maxLength={8}
          disabled={!canEdit}
          required
          className={styles.input}
          externalError={errors.version?.message}
        />
      </div>

    </div>
  );
}
